import { useState } from "react";
import { BookOpen, SearchX } from "lucide-react";
import { Button, EmptyState, ErrorBanner, LoadingText, SearchInput } from "../ui";
import ReportCard from "./ReportCard";
import ReportFilterControl from "./ReportFilterControl";
import type { ReportFilter } from "./ReportFilterControl";
import type { UserReport } from "../../types/reports";

const EMPTY_CONTENT: Record<ReportFilter, { title: string; description: string }> = {
  all: {
    title: "No reports yet",
    description: "Create your first report or discover reports published by other Brian users.",
  },
  created: {
    title: "No reports created by you",
    description: "Create a report manually or ask Brian to help you build one.",
  },
  subscribed: {
    title: "No subscribed reports",
    description: "Subscribe to public reports to keep them in your personal collection.",
  },
  shared: {
    title: "No reports shared with you",
    description: "Reports that other users share directly with you will appear here.",
  },
};

type MyReportsPanelProps = {
  reports: UserReport[];
  createdByMe: UserReport[];
  subscribed: UserReport[];
  sharedWithMe: UserReport[];
  loading: boolean;
  error: string;
  filter: ReportFilter;
  onFilterChange: (filter: ReportFilter) => void;
  onRefresh: () => void;
  onCreate: () => void;
  onDiscover: () => void;
};

export default function MyReportsPanel({
  reports,
  createdByMe,
  subscribed,
  sharedWithMe,
  loading,
  error,
  filter,
  onFilterChange,
  onRefresh,
  onCreate,
  onDiscover,
}: MyReportsPanelProps) {
  const [search, setSearch] = useState("");

  if (loading) return <LoadingText>Loading...</LoadingText>;
  if (error) return <ErrorBanner>{error}</ErrorBanner>;

  const visible =
    filter === "created"
      ? createdByMe
      : filter === "subscribed"
        ? subscribed
        : filter === "shared"
          ? sharedWithMe
          : reports;

  const query = search.trim().toLowerCase();
  const filtered = visible.filter(
    (report) =>
      !query ||
      report.name.toLowerCase().includes(query) ||
      (report.description ?? "").toLowerCase().includes(query),
  );

  return (
    <>
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <ReportFilterControl
          value={filter}
          onChange={onFilterChange}
          counts={{
            all: reports.length,
            created: createdByMe.length,
            subscribed: subscribed.length,
            shared: sharedWithMe.length,
          }}
        />
        {reports.length > 0 && (
          <div className="w-full sm:w-64">
            <SearchInput
              id="my-reports-search"
              label="Search reports"
              placeholder="Search reports…"
              value={search}
              onChange={setSearch}
            />
          </div>
        )}
      </div>
      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((report) => (
            <ReportCard key={report.id} report={report} onRefresh={onRefresh} />
          ))}
        </div>
      ) : visible.length > 0 ? (
        <EmptyState
          bordered
          icon={<SearchX size={30} aria-hidden="true" />}
          title="No matches"
          description="No reports match your search."
        />
      ) : (
        <EmptyState
          bordered
          icon={<BookOpen size={30} aria-hidden="true" />}
          title={EMPTY_CONTENT[filter].title}
          description={EMPTY_CONTENT[filter].description}
          action={
            <div className="flex flex-wrap justify-center gap-2">
              {(filter === "all" || filter === "created") && (
                <Button onClick={onCreate}>Create report</Button>
              )}
              {(filter === "all" || filter === "subscribed") && (
                <Button variant="secondary" onClick={onDiscover}>
                  Discover reports
                </Button>
              )}
            </div>
          }
        />
      )}
    </>
  );
}
